/**
 * ThinkingState — Carnaf design system v1.0
 * Spec: docs/strategy/car-as-place/design-system-2026-05-22/04-components.md § 8
 *
 * AI-active visual. Used while OCR is processing, while the conversational AI
 * is composing, anywhere an LLM call is in flight. Status text is *specific*
 * to the work being done — never generic "Thinking…" / "Loading…".
 *
 * Status copy patterns (from spec):
 *   OCR pipeline:
 *     1. קוראים את המסמך…       (Reading the document)
 *     2. בודקים תאריכים…         (Checking dates)
 *     3. כמעט סיימנו…            (Almost done)
 *   AI conversation:
 *     1. מחפשים במידע שלך…       (Searching your info)
 *     2. קוראים את {filename}…   (Reading {file})
 *     3. answer streams
 */

import { useEffect, useState } from 'react';

interface ThinkingStateProps {
  /** Single status string OR an array of strings that cycle every `cycleMs` ms. */
  status: string | string[];
  /** Interval between status crossfades when an array is passed. */
  cycleMs?: number;
  /** Wrap in a card? Default true. */
  inCard?: boolean;
}

export function ThinkingState({ status, cycleMs = 3000, inCard = true }: ThinkingStateProps): JSX.Element {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!Array.isArray(status)) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % status.length);
    }, cycleMs);
    return () => clearInterval(id);
  }, [status, cycleMs]);

  const currentStatus = Array.isArray(status) ? status[index] : status;

  const content = (
    <div className="flex flex-col items-center text-center gap-3 py-4">
      <div
        className="w-6 h-6 rounded-full border-2 border-carnaf-line"
        style={{
          borderTopColor: '#0D8C6C',
          animation: 'spin 0.9s linear infinite',
        }}
      />
      <p className="text-body text-carnaf-ink-muted animate-fade-in" key={currentStatus}>
        {currentStatus}
      </p>
    </div>
  );

  if (!inCard) return content;
  return (
    <div className="rounded-xl bg-white border border-carnaf-line p-6">
      {content}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
